import { getAccessToken } from "../lib/auth";
import { parseApiError } from "../lib/api-error";

const API_BASE = "/api";

export interface EnvironmentSpec {
  app: string;
  envName: string;
  tier?: string;
  dbSchema?: string;
  clickhouseDb?: string;
  keycloakClient?: string;
  secretBundle?: string;
  hostnames?: string[];
  temporalNamespace?: string;
  replicas?: { api?: number; worker?: number };
  labels?: Record<string, string>;
}

export interface EnvironmentStatus {
  phase?: "Pending" | "Ready" | "Degraded" | "Archived";
  conditions?: Array<{
    type: string;
    status: string;
    reason?: string;
    message?: string;
    lastTransitionTime?: string;
  }>;
  workloads?: { api?: string; worker?: string };
  lastReconcileTime?: string;
}

export interface EnvironmentResource {
  metadata: {
    name: string;
    namespace: string;
    uid?: string;
    creationTimestamp?: string;
    labels?: Record<string, string>;
    annotations?: Record<string, string>;
  };
  spec: EnvironmentSpec;
  status?: EnvironmentStatus;
}

export interface WorkloadSummary {
  kind: "Deployment";
  name: string;
  component?: string;
  replicas: number;
  available: number;
  ready: number;
  updated: number;
}

export interface PodSummary {
  name: string;
  phase: string;
  nodeName?: string;
  startTime?: string;
}

export interface EnvironmentDetail extends EnvironmentResource {
  argocd?: {
    name: string;
    syncStatus?: string;
    healthStatus?: string;
    revision?: string;
    syncedAt?: string;
  };
  workloads: WorkloadSummary[];
  pods: PodSummary[];
}

async function fetchAPI<T>(endpoint: string): Promise<T> {
  const token = await getAccessToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(`${API_BASE}${endpoint}`, { headers });
  if (!response.ok) throw await parseApiError(response);
  return response.json();
}

export const environmentsApi = {
  list: () =>
    fetchAPI<{ items: EnvironmentResource[] }>(`/applications`),
  listByApp: (app: string) =>
    fetchAPI<{ app: string; items: EnvironmentResource[] }>(`/applications/${app}`),
  get: (app: string, envName: string) =>
    fetchAPI<EnvironmentDetail>(`/applications/${app}/${envName}`),
  workloads: (app: string, envName: string) =>
    fetchAPI<{
      app: string;
      envName: string;
      workloads: WorkloadSummary[];
      pods: PodSummary[];
    }>(`/applications/${app}/${envName}/workloads`),
};
